import React, { useState, useEffect } from 'react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { CountryDropdown } from "react-country-region-selector";
import confetti from "canvas-confetti";




/* ---------- SVG icons ---------- */
const IcUser = (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
        <path d="M20 21a8 8 0 0 0-16 0" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);
const IcBuilding = (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
        <path d="M3 21h18M4 21V8a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v13M9 21v-5h2v5M8 10h2M8 14h2M14 14h2M14 10h2" />
    </svg>
);
const IcSettings = (props) => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
        <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82h0a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82h0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);
const IcCheck = (props) => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
);

/* ---------- Signup component ---------- */
const Signup = () => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        name: '',
        company: '',
        email: '',
        phone: '', // will store full phone from PhoneInput
        businessCategory: '',
        businessType: '',
        outlets: '',
        deployment: '',
    });
    const [emailError, setEmailError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [success, setSuccess] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        // Fetch user's country via IP
        fetch("https://ipapi.co/json/")
            .then((res) => res.json())
            .then((data) => {
                if (data.country_name && data.country_calling_code) {
                    setFormData((prev) => ({
                        ...prev,
                        country: data.country_name,
                        phone: data.country_calling_code, // prefill phone code
                    }));
                }
            })
            .catch((err) => console.error("Could not detect country:", err));
    }, []);

    // business/category helpers
    const businessCategories = {
        'Food & Beverage': ['Restaurant', 'Café', 'Bakery', 'Food Truck', 'Bar / Pub'],
        Retail: ['Retail Store', 'Supermarket', 'Chain / Franchise', 'Pharmacy', 'Electronics Store'],
        Services: ['Salon', 'Spa', 'Gym / Fitness', 'Laundry', 'Repair Shop'],
        Hospitality: ['Hotel', 'Motel', 'Resort', 'Guesthouse'],
        Healthcare: ['Clinic', 'Pharmacy', 'Diagnostics Center'],
        Others: ['Wholesale / Distribution', 'E-commerce', 'Other'],
    };
    const outletRelevantTypes = ['Retail Store', 'Supermarket', 'Chain / Franchise', 'Hotel', 'Motel', 'Resort', 'Guesthouse'];
    const outletOptions = ['1', '2–5', '6–10', '11–20', '21+'];

    const handleField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

    const validateStep1 = () => {
        const newErrors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!formData.name.trim()) newErrors.name = "Full name is required.";
        if (!formData.company.trim()) newErrors.company = "Company name is required.";
        if (!emailRegex.test(formData.email || "")) newErrors.email = "Please enter a valid email.";
        if (!formData.phone || formData.phone.replace(/\D/g, "").length < 7)
            newErrors.phone = "Please enter a valid phone number.";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep2 = () => {
        const newErrors = {};
        if (!formData.businessCategory) newErrors.businessCategory = "Select your business category.";
        if (!formData.businessType) newErrors.businessType = "Select your business type.";
        if (outletRelevantTypes.includes(formData.businessType) && !formData.outlets)
            newErrors.outlets = "Select the number of outlets.";

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateStep3 = () => {
        const newErrors = {};
        if (!formData.deployment) newErrors.deployment = "Select your deployment preference.";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleNext = (e) => {
        e?.preventDefault();

        if (step === 1 && !validateStep1()) return;
        if (step === 2 && !validateStep2()) return;
        if (step === 3 && !validateStep3()) return;

        setStep((s) => Math.min(4, s + 1));
    };

    const handleBack = () => setStep((s) => Math.max(1, s - 1));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.email || !formData.phone || !formData.company || !formData.country) {
            alert("Please fill all required fields");
            return;
        }
        if (!validateStep1() || !validateStep2() || !validateStep3()) return;
        try {
            const response = await fetch("https://freetrial.armadapos.com/data/form-submit.php", {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                },
                body: new URLSearchParams(formData).toString(),
            });

            const result = await response.json();

            if (result.status === "success") {
                console.log("Form submitted successfully:", result);
                setSuccess(true);
                setStep(5);
            } else {
                console.error("Submission failed:", result.message);
                alert(result.message || "Submission failed");
            }
        } catch (err) {
            console.error("Server error:", err);
            alert("Server error. Please try again later.");
        }
    };

    // Confetti on success (loop ~3s)
    useEffect(() => {
        if (step === 5 && window.confetti) {
            const end = Date.now() + 3000;
            (function frame() {
                window.confetti({ particleCount: 7, angle: 60, spread: 55, origin: { x: 0 } });
                window.confetti({ particleCount: 7, angle: 120, spread: 55, origin: { x: 1 } });
                if (Date.now() < end) requestAnimationFrame(frame);
            })();
        }
    }, [step]);

    const editStep = (target) => setStep(target);

    const Stepper = () => {
        const labels = ['Contact Info', 'Business Info', 'Preferences', 'Review'];
        const icons = [IcUser, IcBuilding, IcSettings, IcCheck];
        const percent = step === 1 ? 25 : step === 2 ? 50 : step === 3 ? 75 : 100;
        return (
            <div className="max-w-xl mx-auto mb-6">
                <div className="flex justify-between items-center mb-2">
                    {labels.map((lab, i) => {
                        const Icon = icons[i];
                        const isActive = step === i + 1;
                        return (
                            <div key={i} className="flex-1 flex flex-col items-center">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-700'}`}>
                                    <Icon />
                                </div>
                                <div className={`mt-2 text-xs ${isActive ? 'text-blue-600 font-semibold' : 'text-gray-700'}`}>{lab}</div>
                            </div>
                        );
                    })}
                </div>
                <div className="w-full bg-gray-300 h-2 rounded">
                    <div className="h-2 rounded bg-blue-600 transition-all" style={{ width: `${percent}%` }} />
                </div>
            </div>
        );
    };

    // review fields order
    const REVIEW_FIELDS = [
        { key: 'name', label: 'Full Name', step: 1 },
        { key: 'company', label: 'Company', step: 1 },
        { key: 'email', label: 'Email', step: 1 },
        { key: 'phone', label: 'Phone', step: 1 },
        { key: 'businessCategory', label: 'Business Category', step: 2 },
        { key: 'businessType', label: 'Business Type', step: 2 },
        { key: 'outlets', label: 'Number of Outlets', step: 2 },
        { key: 'deployment', label: 'Deployment', step: 3 },
    ];

    const [fadeOut, setFadeOut] = useState(false);

    const handleExitSuccess = () => {
        setFadeOut(true);
        setTimeout(() => {
            setFormData({
                name: '',
                company: '',
                email: '',
                phone: '',
                businessCategory: '',
                businessType: '',
                outlets: '',
                deployment: '',
            });
            setStep(1);
            setSuccess(false);
            setFadeOut(false);
        }, 600); // match animation duration
    };

    // Confetti on success (loop ~3s)
    useEffect(() => {
        if (step === 5 && window.confetti) {
            const duration = 4000; // 4 seconds
            const animationEnd = Date.now() + duration;

            (function frame() {
                window.confetti({
                    particleCount: 5,
                    startVelocity: 30,
                    spread: 360,
                    ticks: 60,
                    origin: { x: Math.random(), y: 0 } // fall from top random X
                });

                if (Date.now() < animationEnd) {
                    requestAnimationFrame(frame);
                }
            })();
        }
    }, [step]);


    return (
        <section id="signup" aria-label="Signup" className="py-20 px-6 bg-blue-600 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">Start Your Free, Risk-Free Trial of Armada POS</h2>
            <p className="mb-6 max-w-2xl mx-auto">Experience the full power of Armada POS — no commitment, no credit card needed.</p>

            {step <= 4 && <Stepper />}

            {step <= 4 ? (
                <form
                    onSubmit={step === 4 ? handleSubmit : handleNext}
                    className="bg-white text-gray-800 max-w-xl mx-auto rounded-xl shadow px-8 py-10 text-left space-y-6"
                >
                    {/* Step 1 */}
                    {step === 1 && (
                        <>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block mb-2 font-medium">Full Name</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => handleField('name', e.target.value)}
                                        required
                                        className="w-full border rounded-lg px-4 py-2"
                                    />
                                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                                </div>
                                <div>
                                    <label className="block mb-2 font-medium">Company Name</label>
                                    <input
                                        type="text"
                                        value={formData.company}
                                        onChange={(e) => handleField('company', e.target.value)}
                                        required
                                        className="w-full border rounded-lg px-4 py-2"
                                    />
                                    {errors.company && <p className="text-red-500 text-sm mt-1">{errors.company}</p>}

                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block mb-2 font-medium">Business Email</label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleField('email', e.target.value)}
                                        required
                                        className="w-full border rounded-lg px-4 py-2"
                                    />
                                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                                </div>

                                <div>
                                    <label className="block mb-2 font-medium">Phone</label>
                                    <PhoneInput
                                        defaultCountry={formData.country}
                                        value={formData.phone}
                                        onChange={(phone) => handleField('phone', phone)}
                                        inputClass="w-full border rounded-lg px-4 py-2"
                                    />
                                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}

                                </div>
                            </div>
                            <div>
                                <label className="block mb-2 font-medium">Country</label>
                                <CountryDropdown
                                    value={formData.country}
                                    onChange={(val) => handleField("country", val)}
                                    className="w-full border rounded-lg px-4 py-2"
                                />
                            </div>
                        </>
                    )}

                    {/* Step 2 */}
                    {step === 2 && (
                        <>
                            <div>
                                <label className="block mb-2 font-medium">Business Category</label>
                                <select
                                    value={formData.businessCategory}
                                    onChange={(e) => {
                                        handleField('businessCategory', e.target.value);
                                        // reset businessType/outlets when category changes
                                        handleField('businessType', '');
                                        handleField('outlets', '');
                                    }}
                                    required
                                    className="w-full border rounded-lg px-4 py-2"
                                >
                                    <option value="">Select category</option>
                                    {Object.keys(businessCategories).map((cat) => (
                                        <option key={cat} value={cat}>
                                            {cat}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {formData.businessCategory && (
                                <div>
                                    <label className="block mb-2 font-medium">Business Type</label>
                                    <select
                                        value={formData.businessType}
                                        onChange={(e) => handleField('businessType', e.target.value)}
                                        required
                                        className="w-full border rounded-lg px-4 py-2"
                                    >
                                        <option value="">Select type</option>
                                        {businessCategories[formData.businessCategory].map((t) => (
                                            <option key={t} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {outletRelevantTypes.includes(formData.businessType) && (
                                <div>
                                    <label className="block mb-2 font-medium">Number of Outlets</label>
                                    <select
                                        value={formData.outlets}
                                        onChange={(e) => handleField('outlets', e.target.value)}
                                        required
                                        className="w-full border rounded-lg px-4 py-2"
                                    >
                                        <option value="">Select outlets</option>
                                        {outletOptions.map((o) => (
                                            <option key={o} value={o}>
                                                {o}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </>
                    )}

                    {/* Step 3 */}
                    {step === 3 && (
                        <div>
                            <label className="block mb-2 font-medium">Deployment Preference</label>
                            <select
                                value={formData.deployment}
                                onChange={(e) => handleField('deployment', e.target.value)}
                                required
                                className="w-full border rounded-lg px-4 py-2"
                            >
                                <option value="">Select deployment</option>
                                <option>Cloud</option>
                                <option>On-Premise</option>
                                <option>Hybrid</option>
                            </select>
                        </div>
                    )}

                    {/* Step 4 (Review) */}
                    {step === 4 && (
                        <div>
                            <h3 className="text-xl font-bold mb-4">Review Your Details</h3>
                            <div className="bg-gray-50 border rounded-lg p-6 space-y-4">
                                {REVIEW_FIELDS.map(({ key, label, step: target }) => {
                                    const val = formData[key];
                                    if (!val) return null;
                                    return (
                                        <div key={key} className="grid grid-cols-3 gap-4 items-center">
                                            <div className="font-medium text-gray-700">{label}</div>
                                            <div className="text-gray-900">{val}</div>
                                            <div className="justify-self-end">
                                                <button type="button" onClick={() => editStep(target)} className="text-blue-600 text-sm hover:underline">
                                                    Edit
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Buttons */}
                    <div className="flex justify-between pt-2">
                        {step > 1 ? (
                            <button type="button" onClick={handleBack} className="px-4 py-2 rounded-lg bg-gray-200 text-gray-800 hover:bg-gray-300">
                                Back
                            </button>
                        ) : (
                            <div />
                        )}

                        <button
                            type="submit"
                            className="ml-auto px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                        >
                            {step === 4 ? 'Confirm & Start Trial' : 'Next'}
                        </button>
                    </div>
                </form>
            ) : (

                <div
                    className={`flex flex-col items-center justify-center text-center py-24 px-6 bg-blue-50 min-h-[70vh] rounded-xl shadow-lg ${fadeOut ? "animate-fadeOut" : "animate-fadeIn"
                        }`}
                >
                    <img
                        src="https://www.armadapos.com/images/Armada_Infotech_Logo.png"
                        alt="Armada POS Logo"
                        className="w-40 mb-6"
                    />
                    <div className="flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-6 text-green-600">
                        <IcCheck width="48" height="48" />
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">
                        Thank You for Choosing Armada POS 🎉
                    </h3>
                    <p className="text-gray-700 max-w-2xl mb-8">
                        Your free trial request has been successfully submitted. Please check
                        your email for login details and setup instructions. Our team may also
                        contact you via phone/WhatsApp to help with onboarding.
                    </p>
                    <div className="flex flex-wrap gap-4 justify-center">
                        <a
                            href="#features"
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
                        >
                            Explore Features
                        </a>
                        <a
                            href="#faq"
                            className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300"
                        >
                            View FAQs
                        </a>
                        <button
                            onClick={handleExitSuccess}
                            className="px-6 py-3 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600"
                        >
                            Back to Home
                        </button>
                    </div>
                    <p className="mt-10 text-sm text-gray-500">
                        Need help? Contact us anytime via <strong>WhatsApp, phone, or email</strong>.
                        Our 24×7 support team is here for you.
                    </p>
                </div>
            )}
        </section>
    );
};

export default Signup;
